import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Users, Star, Zap, AlertTriangle, CheckCircle2, Clock, BarChart3 } from 'lucide-react';

export default function ResourcesPage() {
  const { api } = useAuth();
  const [resources, setResources] = useState([]);
  const [ghostAlerts, setGhostAlerts] = useState([]);
  const [deadlineAlerts, setDeadlineAlerts] = useState([]);
  const [tab, setTab] = useState('availability');
  const [recSkill, setRecSkill] = useState('Animation');
  const [recommendations, setRecommendations] = useState([]);

  useEffect(() => {
    api.get('/resources/availability').then(r => setResources(r.data)).catch(() => {});
    api.get('/alerts/ghosting').then(r => setGhostAlerts(r.data)).catch(() => {});
    api.get('/alerts/deadlines').then(r => setDeadlineAlerts(r.data)).catch(() => {});
  }, [api]);

  const getRecommendations = async () => {
    const res = await api.post('/resources/recommend', { skill: recSkill, complexity: 3 });
    setRecommendations(res.data);
  };

  return (
    <div className="space-y-6" data-testid="resources-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-white">Resource Allocation</h1>
        <p className="text-xs text-zinc-500 mt-1">Freelancer availability, alerts, and smart matching</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-800 pb-0">
        {[{k:'availability',l:'Availability'},{k:'alerts',l:`Alerts (${ghostAlerts.length + deadlineAlerts.length})`},{k:'matching',l:'Smart Matching'}].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
            className={`px-4 py-2 text-xs font-medium transition-colors border-b-2 -mb-px ${tab === t.k ? 'border-blue-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === 'availability' && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
          <table className="w-full" data-testid="resources-table">
            <thead>
              <tr className="border-b border-zinc-800 text-[10px] text-zinc-500 uppercase tracking-wider">
                <th className="text-left px-4 py-2.5 font-medium">Freelancer</th>
                <th className="text-left px-4 py-2.5 font-medium">Skills</th>
                <th className="text-left px-4 py-2.5 font-medium">Score</th>
                <th className="text-left px-4 py-2.5 font-medium">Active</th>
                <th className="text-left px-4 py-2.5 font-medium">Capacity</th>
                <th className="text-left px-4 py-2.5 font-medium">NDA</th>
                <th className="text-left px-4 py-2.5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/30">
              {resources.map(r => (
                <tr key={r.id} className="hover:bg-zinc-800/20 transition-colors" data-testid={`resource-${r.id}`}>
                  <td className="px-4 py-2.5">
                    <p className="text-xs text-zinc-200">{r.name}</p>
                    <p className="text-[10px] font-mono text-zinc-600">{r.email}</p>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {r.skills?.map(s => <span key={s} className="text-[9px] px-1.5 py-0.5 bg-zinc-800 text-zinc-400 rounded">{s}</span>)}
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1">
                      <Star className="w-3 h-3 text-amber-400" />
                      <span className="text-xs font-mono text-zinc-300">{r.performance_score}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-xs font-mono text-zinc-400">{r.active_tasks}/5</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${r.capacity > 2 ? 'bg-emerald-500' : r.capacity > 0 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${(r.capacity / 5) * 100}%` }} />
                      </div>
                      <span className="text-[10px] font-mono text-zinc-500">{r.capacity} slots</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5"><span className={`status-${r.nda_status === 'signed' ? 'completed' : 'pending'} px-1.5 py-0.5 rounded-full text-[9px]`}>{r.nda_status}</span></td>
                  <td className="px-4 py-2.5"><span className={`${r.available ? 'text-emerald-400' : 'text-red-400'} text-[10px] font-medium`}>{r.available ? 'Available' : 'Full'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'alerts' && (
        <div className="space-y-4">
          {/* Ghost Alerts */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Unresponsive Artists ({ghostAlerts.length})</h3>
            </div>
            <div className="divide-y divide-zinc-800/30">
              {ghostAlerts.map(a => (
                <div key={a.task_id} className="px-4 py-3 flex items-center justify-between" data-testid={`ghost-alert-${a.task_id}`}>
                  <div>
                    <p className="text-xs text-zinc-200">{a.task_name}</p>
                    <p className="text-[10px] text-zinc-600">Assigned to: {a.assignee_name} | Silent for {a.hours_silent}hrs</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-medium ${a.severity === 'high' ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'}`}>{a.severity}</span>
                </div>
              ))}
              {ghostAlerts.length === 0 && <p className="text-xs text-zinc-600 px-4 py-6 text-center">No ghosting alerts</p>}
            </div>
          </div>

          {/* Deadline Alerts */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-400" />
              <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Due Within 48hrs ({deadlineAlerts.length})</h3>
            </div>
            <div className="divide-y divide-zinc-800/30">
              {deadlineAlerts.map(t => (
                <div key={t.id} className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-zinc-200">{t.name}</p>
                    <p className="text-[10px] text-zinc-600">{t.assignee_name} | Due: {t.due_date ? new Date(t.due_date).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <span className={`status-${t.status} px-1.5 py-0.5 rounded-full text-[9px] font-medium`}>{t.status?.replace('_',' ')}</span>
                </div>
              ))}
              {deadlineAlerts.length === 0 && <p className="text-xs text-zinc-600 px-4 py-6 text-center">No deadline alerts</p>}
            </div>
          </div>
        </div>
      )}

      {tab === 'matching' && (
        <div className="space-y-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-3">AI Freelancer Matching</h3>
            <div className="flex gap-2">
              <select data-testid="skill-select" value={recSkill} onChange={e => setRecSkill(e.target.value)} className="h-8 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-zinc-300 px-3 focus:outline-none">
                {['Animation', '2D Animation', 'Character Design', 'Compositing', 'Lighting', 'Layout', 'Rigging', 'VFX', 'Storyboard'].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <button data-testid="find-match-btn" onClick={getRecommendations} className="h-8 px-4 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-sm flex items-center gap-2">
                <Zap className="w-3.5 h-3.5" /> Find Match
              </button>
            </div>
          </div>

          {recommendations.length > 0 && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
              <div className="px-4 py-3 border-b border-zinc-800">
                <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Recommendations for "{recSkill}"</h3>
              </div>
              <div className="divide-y divide-zinc-800/30">
                {recommendations.map((r, i) => (
                  <div key={r.id} className="px-4 py-3 flex items-center justify-between" data-testid={`recommendation-${i}`}>
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-zinc-800 rounded-sm flex items-center justify-center text-[10px] font-bold text-zinc-300">#{i + 1}</span>
                      <div>
                        <p className="text-xs text-zinc-200">{r.name}</p>
                        <div className="flex gap-1 mt-0.5">
                          {r.skills?.slice(0, 3).map(s => <span key={s} className="text-[8px] px-1 py-0.5 bg-zinc-800 text-zinc-500 rounded">{s}</span>)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-xs font-mono text-emerald-400">{r.score}</p>
                        <p className="text-[9px] text-zinc-600">match score</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-mono text-zinc-400">{r.active_tasks}/5</p>
                        <p className="text-[9px] text-zinc-600">load</p>
                      </div>
                      {r.skill_match && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
